/**
 * Folder Adapter
 * Converts web-app folder data to the new Folder and TreeFolder types
 */

import type { TreeFolder } from '../components/FolderTree/FolderTree.types';
import type { Folder } from '../types';

/**
 * Web-app folder structure (from appstore.js and foldercard.jsx)
 */
export interface WebAppFolder {
  _id: string;
  name: string;
  thumbnail?: string;
  folders_count?: number;
  resources_count?: number;
  isPublic?: boolean;
  allowedActions?: string[];
  defaultorder?: string;
}

/**
 * Convert a single web-app folder object to the new Folder interface
 * 
 * @param folder - Web-app folder object
 * @returns Folder object compatible with new components
 * 
 * @example
 * ```typescript
 * const webAppFolder = {
 *   _id: 'folder123',
 *   name: 'My Folder',
 *   thumbnail: 'https://...',
 *   folders_count: 5,
 *   resources_count: 20,
 *   isPublic: true,
 *   allowedActions: ['list', 'view']
 * };
 * 
 * const folder = adaptFolder(webAppFolder);
 * // Returns: { id: 'folder123', name: 'My Folder', foldersCount: 5, ... }
 * ```
 */
export function adaptFolder(folder: WebAppFolder): Folder {
  return {
    id: folder._id,
    name: folder.name,
    thumbnail: folder.thumbnail,
    foldersCount: folder.folders_count ?? 0,
    resourcesCount: folder.resources_count ?? 0,
    isPublic: folder.isPublic ?? true,
    allowedActions: folder.allowedActions ?? [],
    defaultOrder: folder.defaultorder,
  };
}

/**
 * Convert an array of web-app folder objects to Folder array
 * 
 * @param folders - Array of web-app folder objects
 * @returns Array of Folder objects compatible with new components
 * 
 * @example
 * ```typescript
 * const folders = appStore.gridData.folder[0] || [];
 * const adaptedFolders = adaptFolders(folders);
 * ```
 */
export function adaptFolders(folders: WebAppFolder[]): Folder[] {
  if (!Array.isArray(folders)) {
    return [];
  }
  return folders.map(adaptFolder);
}

/**
 * Convert a single web-app folder to a TreeFolder for hierarchical navigation
 * 
 * @param folder - Web-app folder object
 * @param parentId - Parent folder ID (null for root folders)
 * @param children - Pre-loaded child folders (optional)
 * @returns TreeFolder object compatible with FolderTree component
 * 
 * @example
 * ```typescript
 * const webAppFolder = {
 *   _id: 'folder123',
 *   name: 'My Folder',
 *   folders_count: 3
 * };
 * 
 * const treeFolder = adaptFolderToTree(webAppFolder, null);
 * // Returns: { id: 'folder123', name: 'My Folder', parentId: null, ... }
 * ```
 */
export function adaptFolderToTree(
  folder: WebAppFolder,
  parentId: string | null = null,
  children?: TreeFolder[]
): TreeFolder {
  return {
    id: folder._id,
    name: folder.name,
    parentId,
    children,
    foldersCount: folder.folders_count ?? 0,
    resourcesCount: folder.resources_count ?? 0,
    isPublic: folder.isPublic ?? true,
  };
}

/**
 * Convert an array of web-app folders to TreeFolder array
 * 
 * @param folders - Array of web-app folder objects
 * @param parentId - Parent folder ID (null for root folders)
 * @returns Array of TreeFolder objects compatible with FolderTree component
 * 
 * @example
 * ```typescript
 * const folders = appStore.gridData.folder[0] || [];
 * const treeFolders = adaptFoldersToTree(folders, null);
 * ```
 */
export function adaptFoldersToTree(
  folders: WebAppFolder[],
  parentId: string | null = null
): TreeFolder[] {
  if (!Array.isArray(folders)) {
    return [];
  }
  return folders.map((folder) => adaptFolderToTree(folder, parentId));
}

/**
 * Extended folder type with parent reference for tree building
 */
export interface WebAppFolderWithParent extends WebAppFolder {
  parentId?: string;
}

/**
 * Build a hierarchical tree from flat folder data
 * This is useful when you have a flat list of folders with parent references
 *
 * @param folders - Flat array of web-app folders with parent references
 * @returns Hierarchical TreeFolder array
 */
export function buildFolderTree(
  folders: WebAppFolderWithParent[]
): TreeFolder[] {
  // Create a map for quick lookup
  const folderMap = new Map<string, TreeFolder>();
  const rootFolders: TreeFolder[] = [];

  // First pass: create all TreeFolder objects
  for (const folder of folders) {
    folderMap.set(folder._id, adaptFolderToTree(folder, folder.parentId ?? null, []));
  }

  // Second pass: build the hierarchy
  for (const folder of folders) {
    const treeFolder = folderMap.get(folder._id);
    if (!treeFolder) continue;

    if (folder.parentId) {
      const parentFolder = folderMap.get(folder.parentId);
      if (parentFolder && parentFolder.children) {
        parentFolder.children.push(treeFolder);
      } else {
        // Parent not found, treat as root
        rootFolders.push(treeFolder);
      }
    } else {
      rootFolders.push(treeFolder);
    }
  }

  return rootFolders;
}

/**
 * Type guard to check if an object is a valid WebAppFolder
 */
export function isWebAppFolder(obj: unknown): obj is WebAppFolder {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  
  const folder = obj as Record<string, unknown>;
  return typeof folder._id === 'string' && typeof folder.name === 'string';
}